<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ListAttribute from 'dashboard/components-next/CustomAttributes/ListAttribute.vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';
import DateAttribute from 'dashboard/components-next/CustomAttributes/DateAttribute.vue';
import OtherAttribute from 'dashboard/components-next/CustomAttributes/OtherAttribute.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  contactId: {
    type: Number,
    default: null,
  },
});

const store = useStore();
const { t } = useI18n();
const route = useRoute();

const handleDelete = async () => {
  try {
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: props.contactId || route.params.contactId,
      customAttributes: [props.attribute.attributeKey],
    });
    useAlert(
      t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.DELETE_SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.DELETE_ERROR')
    );
  }
};

const handleUpdate = async value => {
  try {
    await store.dispatch('contacts/update', {
      id: props.contactId || route.params.contactId,
      customAttributes: {
        [props.attribute.attributeKey]: value,
      },
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
    );
  }
};

const componentMap = {
  list: ListAttribute,
  checkbox: CheckboxAttribute,
  date: DateAttribute,
  default: OtherAttribute,
};

const CurrentAttributeComponent = computed(() => {
  return (
    componentMap[props.attribute.attributeDisplayType] || componentMap.default
  );
});
</script>

<template>
  <div
    class="group/attribute items-center w-full gap-2"
    :class="[
      compact
        ? 'flex justify-between min-h-11 px-3 py-2.5'
        : 'grid grid-cols-[140px,1fr]',
      isEditingView && !compact ? 'min-h-10' : '',
      !isEditingView && !compact ? 'min-h-11' : '',
    ]"
  >
    <div
      class="flex items-center min-w-0"
      :class="
        compact
          ? 'gap-2.5 flex-shrink-0 max-w-[45%]'
          : 'justify-between truncate'
      "
    >
      <span
        v-if="compact"
        class="flex-shrink-0 i-lucide-tag size-4 text-n-slate-11"
      />
      <span
        :class="[
          compact ? 'break-words' : 'truncate',
          compact
            ? 'text-sm font-normal text-n-slate-12'
            : 'text-sm font-medium text-n-slate-12',
        ]"
      >
        {{ attribute.attributeDisplayName }}
      </span>
    </div>

    <div class="flex items-center justify-end flex-1 min-w-0 gap-1.5">
      <component
        :is="CurrentAttributeComponent"
        :attribute="attribute"
        :is-editing-view="isEditingView && !compact"
        :compact="compact"
        :class="{ 'min-h-0': compact }"
        @update="handleUpdate"
        @delete="handleDelete"
      />
      <span
        v-if="compact && attribute.attributeDisplayType !== 'checkbox'"
        class="flex-shrink-0 i-lucide-chevron-right size-3.5 text-n-slate-9"
      />
    </div>
  </div>
</template>
